// Marketecture — three columns (Inputs · Studio · Runtime) with a tiny graph
// inside the centre cell. Communicates "design here, run anywhere".

import { Logo } from "@/components/ui";

const INPUTS = ["PRD docs", "Prompts", "Existing tools", "API keys"];
const RUNTIME = ["LangGraph", "Mastra", "TS · Python", "Your CI/CD"];

const BULLETS: Array<[string, string]> = [
  [
    "SKETCH",
    "Drag agents, tools, branches, and human gates onto a canvas. The graph is the thinking.",
  ],
  [
    "EXPORT",
    "Seven formats: LangGraph, CrewAI, AutoGen, Python, JSON, Mermaid, Markdown. Diffable, reviewable, yours.",
  ],
  [
    "PORT",
    "Run the exported harness on the platform you already use. No lock-in, no hosted runtime to learn.",
  ],
];

export default function HomeMarketecture() {
  return (
    <section
      id="how"
      className="mx-auto max-w-[1320px] border-t border-border px-7 py-16"
    >
      <div className="grid grid-cols-[1fr_1.6fr] items-center gap-16">
        <div>
          <div className="mb-3.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-muted">
            02 — How it fits
          </div>
          <h2 className="serif mb-[18px] text-[52px] font-normal leading-none tracking-[-0.02em] text-ink">
            Designs in Studio.
            <br />
            Runs in your stack.
          </h2>
          <p
            className="m-0 text-[15px] leading-[1.55] text-ink-2"
            style={{ textWrap: "pretty" }}
          >
            The studio sits between the prompts you write and whatever runtime
            you ship to. There&rsquo;s no execution layer here &mdash; design
            the harness, export it as code, and run it on the agent platform you
            already trust.
          </p>
          <ul className="mt-5 flex list-none flex-col gap-2.5 p-0 text-[13px] text-ink-2">
            {BULLETS.map(([k, v]) => (
              <li key={k} className="flex gap-3">
                <span
                  className="w-16 flex-none pt-px font-mono text-[10px] font-semibold tracking-[0.10em]"
                  style={{ color: "var(--accent-deep)" }}
                >
                  {k}
                </span>
                <span className="leading-[1.45]">{v}</span>
              </li>
            ))}
          </ul>
        </div>

        <MarketectureDiagram />
      </div>
    </section>
  );
}

function MarketectureDiagram() {
  return (
    <div
      className="relative grid grid-cols-[1fr_1.4fr_1fr] items-stretch gap-[18px] rounded-2xl border border-border bg-surface p-7"
      style={{
        height: 420,
        backgroundImage:
          "radial-gradient(rgba(20,20,30,0.05) 1px, transparent 1px)",
        backgroundSize: "16px 16px",
      }}
    >
      <Column label="YOUR INPUTS" items={INPUTS} />

      {/* Center — the Studio block */}
      <div
        className="relative flex flex-col gap-3 rounded-[14px] p-[18px]"
        style={{
          background:
            "linear-gradient(180deg, hsl(var(--surface)), hsl(var(--surface-2)))",
          border: "1.5px solid var(--accent-ring)",
          boxShadow:
            "0 0 0 6px var(--accent-soft), 0 12px 30px -10px rgba(20,20,30,0.12)",
        }}
      >
        <div className="flex items-center gap-2">
          <Logo size={20} />
          <span className="text-[12.5px] font-semibold text-ink">
            Harness Studio
          </span>
          <span
            className="ml-auto rounded px-1.5 py-0.5 font-mono text-[9.5px] font-semibold uppercase tracking-[0.10em]"
            style={{
              background: "var(--accent-soft)",
              color: "var(--accent-deep)",
            }}
          >
            The graph layer
          </span>
        </div>

        <div
          className="relative flex-1 overflow-hidden rounded-[10px] border border-border bg-canvas"
          style={{
            backgroundImage:
              "radial-gradient(rgba(20,20,30,0.10) 1px, transparent 1px)",
            backgroundSize: "14px 14px",
          }}
        >
          <MiniCanvas />
        </div>

        <div className="text-[11.5px] leading-[1.4] text-ink-2">
          Sketch it. Export it. Ship it wherever your agents already live.
        </div>
      </div>

      <Column label="YOUR RUNTIME" items={RUNTIME} />

      <Connector x1="33%" x2="36%" label="design" />
      <Connector x1="64%" x2="67%" label="export" />
    </div>
  );
}

function Column({ label, items }: { label: string; items: string[] }) {
  return (
    <div className="flex flex-col gap-2 py-1">
      <div className="mb-1 font-mono text-[10px] font-semibold tracking-[0.10em] text-muted">
        {label}
      </div>
      {items.map((it) => (
        <div
          key={it}
          className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-[12px] text-ink-2"
        >
          <span className="h-1 w-1 rounded-full bg-border-strong" />
          {it}
        </div>
      ))}
    </div>
  );
}

function Connector({
  x1,
  x2,
  label,
}: {
  x1: string;
  x2: string;
  label: string;
}) {
  return (
    <div
      className="pointer-events-none absolute h-0.5 -translate-y-1/2"
      style={{ left: x1, right: `calc(100% - ${x2})`, top: "50%" }}
    >
      <div className="relative h-px bg-border-strong">
        <div
          className="absolute -right-px -top-1 h-0 w-0"
          style={{
            borderTop: "4px solid transparent",
            borderBottom: "4px solid transparent",
            borderLeft: "5px solid hsl(var(--border-strong))",
          }}
        />
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-surface px-1.5 font-mono text-[9.5px] uppercase tracking-[0.08em] text-muted">
          {label}
        </div>
      </div>
    </div>
  );
}

function MiniCanvas() {
  const W = 380;
  const H = 220;
  const NW = 96;
  const NH = 36;
  const nodes = [
    { id: "a", x: 18, y: 90, type: "agent" as const, label: "Spec" },
    { id: "b", x: 130, y: 90, type: "cond" as const, label: "Split" },
    { id: "c", x: 232, y: 30, type: "agent" as const, label: "Coder" },
    { id: "d", x: 232, y: 150, type: "tool" as const, label: "Tests" },
  ];
  const edges: Array<[string, string]> = [
    ["a", "b"],
    ["b", "c"],
    ["b", "d"],
  ];
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  const out = (n: { x: number; y: number }) => ({
    x: n.x + NW,
    y: n.y + NH / 2,
  });
  const inp = (n: { x: number; y: number }) => ({ x: n.x, y: n.y + NH / 2 });
  const path = (a: { x: number; y: number }, b: { x: number; y: number }) => {
    const p = out(a);
    const q = inp(b);
    const dx = Math.max(20, Math.abs(q.x - p.x) * 0.5);
    return `M ${p.x} ${p.y} C ${p.x + dx} ${p.y}, ${q.x - dx} ${q.y}, ${q.x} ${q.y}`;
  };
  const hueOf = (t: "agent" | "cond" | "tool") =>
    t === "agent" ? 268 : t === "tool" ? 175 : 35;
  return (
    <div className="absolute inset-0">
      <svg
        width="100%"
        height="100%"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        aria-hidden
      >
        {edges.map(([f, t], i) => (
          <path
            key={i}
            d={path(byId[f], byId[t])}
            fill="none"
            stroke="rgba(20,20,30,0.3)"
            strokeWidth="1.2"
          />
        ))}
        {nodes.map((n) => {
          const hue = hueOf(n.type);
          const fill =
            n.type === "agent"
              ? "hsl(var(--surface))"
              : `oklch(0.96 0.04 ${hue})`;
          const stroke = `oklch(0.62 0.16 ${hue})`;
          return (
            <g key={n.id}>
              {n.type === "cond" ? (
                <polygon
                  points={`${n.x + NW / 2},${n.y} ${n.x + NW},${n.y + NH / 2} ${n.x + NW / 2},${n.y + NH} ${n.x},${n.y + NH / 2}`}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1"
                />
              ) : n.type === "tool" ? (
                <polygon
                  points={`${n.x + 8},${n.y} ${n.x + NW - 8},${n.y} ${n.x + NW},${n.y + NH / 2} ${n.x + NW - 8},${n.y + NH} ${n.x + 8},${n.y + NH} ${n.x},${n.y + NH / 2}`}
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1"
                />
              ) : (
                <rect
                  x={n.x}
                  y={n.y}
                  width={NW}
                  height={NH}
                  rx="6"
                  fill={fill}
                  stroke={stroke}
                  strokeWidth="1"
                />
              )}
              <text
                x={n.x + NW / 2}
                y={n.y + NH / 2 + 4}
                textAnchor="middle"
                fontSize="11"
                fontWeight="500"
                fill="hsl(var(--ink))"
                fontFamily="Inter, ui-sans-serif, system-ui, sans-serif"
              >
                {n.label}
              </text>
            </g>
          );
        })}
      </svg>
    </div>
  );
}
